import type { ReactNode } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export type TableCellWithPopoverProps = {
  items: string[];
  separator?: string;
  maxVisible?: number;
  renderPopoverContent?: () => ReactNode;
};

/**
 * Renders a comma-separated (by default) list in a table cell. When there are more than
 * {@link TableCellWithPopoverProps.maxVisible} items, shows the first segment plus `...`;
 * click opens a popover with the full list. Use inside tables that toggle row selection on click —
 * the trigger stops propagation so the row does not receive the click.
 */
export function TableCellWithPopover({
  items,
  separator = ', ',
  maxVisible = 5,
  renderPopoverContent,
}: TableCellWithPopoverProps) {
  if (items.length === 0) {
    return '';
  }

  if (items.length <= maxVisible) {
    return <span>{items.join(separator)}</span>;
  }

  const visibleItems = items.slice(0, maxVisible);
  const displayText = visibleItems.join(separator);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <span
          className="cursor-pointer hover:underline"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {displayText}
          {'...'}
        </span>
      </PopoverTrigger>
      <PopoverContent className="max-w-md max-h-96 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {renderPopoverContent ? (
          renderPopoverContent()
        ) : (
          <div className="text-sm whitespace-normal break-words">
            {items.map((item, index) => (
              <span key={index}>
                <span className="whitespace-nowrap">{item}</span>
                {index < items.length - 1 && separator}
              </span>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
